import { useMemo, useState } from 'react';
import { AppCard } from '../../components/ui/AppCard';
import { Badge } from '../../components/ui/Badge';
import { TextArea } from '../../components/ui/TextField';
import { describeMappingRules } from '../../lib/mappingFieldTable';
import { detectSampleFormat, isSampleFormat, parseSample, previewMapping, samplePlaceholder, type PreviewOutputStatus, type PreviewRecordResult } from '../../lib/mappingPreview';
import type { MappingSpec } from '../../lib/mappingEngine';
import type { Mapping } from '../../types';

// Read-only three-pane Mapping preview for the Integration detail shell
// (roadmap #9 Phase 1 - a scoped-down first cut of slice 6, see
// docs/INTEGRATION_TOOL_PHASE0_AUDIT.md §6.6.7). Mirrors the desktop layout
// docs/CLAUDE_ZMIST_INTEGRATION_TOOL_IMPLEMENTATION.md §7.7 describes
// (source sample / signal builder / live preview) but with no editing, no
// save, and no round-trip guarantee - those only matter once an editor
// exists, which this isn't. The evaluation itself is entirely
// mappingEngine.ts/mappingPreview.ts, already parity-tested against the Go
// and C engines (slice 2); this component only renders their output plus a
// static read of spec.rules, it computes nothing new.
//
// There is nowhere a Mapping stores a sample payload (no field for one
// exists on the model), so - same as the Advanced editor's own "Preview"
// panel - a sample has to be pasted in to populate the Source sample and
// Live preview panes. Until one is, both show an honest empty state rather
// than fabricated data. The Signal builder pane needs no sample: it reads
// spec.rules directly, so it renders immediately.
//
// Gated to sourceTypes that actually run spec.rules through the engine:
// json/csv (mappingEngine.ts's own header comment), plus "webhook" -
// backend/internal/webhookrecv/handler.go's genericSignals() calls the
// identical mappingengine.Apply(mapping.Spec, ...) the json/csv paths use
// (see docs/INTEGRATION_TOOL_PHASE0_AUDIT.md's R1 finding: the Go webhook
// path runs the same rule engine, it just wasn't wired into any preview
// yet). A github/stripe-provider webhook has no user-configured spec.rules
// at all (those two providers' Signals come from a fixed backend rule set -
// see GitHubSetupScreen.tsx/stripe.go), so this pane just shows its honest
// "no rules configured" empty state for them, same as it would for a
// generic-provider mapping with an empty rules array - never fabricated.
// Prometheus mappings still have nothing to show (no rules concept at
// all), so they stay excluded. NOTE: MappingManageScreen.tsx's own
// `previewable` (used for its Advanced-editor Preview panel) still checks
// json/csv only - that's a separate, pre-existing inconsistency with its
// own Spec-section copy ("see Preview below" for a webhook connector) that
// this slice doesn't touch; widening it here, where the read-only detail
// shell needs it to make a freshly-created webhook integration useful, was
// judged the safe, in-scope half of that gap to close now.
export function MappingVisualPreview({ mapping }: { mapping: Mapping }) {
  const [sampleText, setSampleText] = useState('');

  const normalizedSourceType = mapping.sourceType.trim().toLowerCase();
  const previewable =
    normalizedSourceType === 'webhook' || isSampleFormat(normalizedSourceType);
  const spec = mapping.spec as MappingSpec;

  const outputRows = useMemo(() => describeMappingRules(spec), [spec]);

  const { records, parseFailure } = useMemo(() => {
    if (sampleText.trim() === '') return { records: [] as ReturnType<typeof parseSample>, parseFailure: '' };
    try {
      return { records: parseSample(normalizedSourceType, sampleText, spec?.recordPath, detectSampleFormat(normalizedSourceType, sampleText, undefined, typeof spec?.format === 'string' ? spec.format : undefined)), parseFailure: '' };
    } catch (err) {
      return { records: [], parseFailure: err instanceof Error ? err.message : 'Could not parse that sample.' };
    }
  }, [sampleText, normalizedSourceType, spec?.recordPath, spec?.format]);

  const results: PreviewRecordResult[] | null = useMemo(() => {
    if (records.length === 0) return null;
    return previewMapping(spec, records, mapping.nickname || 'sample');
  }, [records, spec, mapping.nickname]);

  if (!previewable) {
    return (
      <p className="text-xs text-black/45 dark:text-white/45">
        A visual field-mapping preview isn't available for a <code className="font-mono">{mapping.sourceType || '(no source type)'}</code>{' '}
        mapping - see the raw spec below.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-black/45 dark:text-white/45 -mt-1">
        A read-only view of this mapping's configured rules. Paste a sample record{' '}
        below to also see the source data and the Signals it would produce - nothing here is sent anywhere, and this
        doesn't change the mapping. To edit rules, use the Advanced editor below.
      </p>
      <div className="flex flex-col gap-4 xl:grid xl:grid-cols-3 xl:gap-4">
        <AppCard className="flex flex-col gap-2" compact>
          <span className="text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">
            Source sample
          </span>
          <TextArea
            value={sampleText}
            onChange={(e) => setSampleText(e.target.value)}
            placeholder={samplePlaceholder(normalizedSourceType, typeof spec?.format === 'string' ? spec.format : undefined)}
            className="font-mono text-[12px] min-h-[100px]"
            spellCheck={false}
          />
          {parseFailure && <p className="text-xs text-red-500">{parseFailure}</p>}
          {!parseFailure && records.length > 0 && (
            <pre className="rounded-lg bg-black/[0.04] dark:bg-white/[0.06] p-2 overflow-x-auto text-[11px] font-mono leading-relaxed m-0 max-h-40 overflow-y-auto">
              {JSON.stringify(records[0], null, 2)}
              {records.length > 1 && `\n\n… +${records.length - 1} more record${records.length - 1 === 1 ? '' : 's'}`}
            </pre>
          )}
          {!parseFailure && records.length === 0 && sampleText.trim() === '' && (
            <p className="text-xs text-black/40 dark:text-white/40">No sample pasted yet.</p>
          )}
        </AppCard>

        <AppCard className="flex flex-col gap-2" compact>
          <span className="text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">
            Signal builder
          </span>
          {outputRows.every((o) => o.rows.length === 0) ? (
            <p className="text-xs text-black/40 dark:text-white/40">
              No field rules configured yet - add <code className="font-mono">spec.rules</code> in the Advanced editor.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {outputRows.map((output) => (
                <div key={output.outputIndex} className="flex flex-col gap-1">
                  {outputRows.length > 1 && (
                    <span className="text-[11px] font-medium text-black/40 dark:text-white/40">
                      Output {output.outputIndex + 1}
                    </span>
                  )}
                  <div className="overflow-x-auto -mx-1">
                    <table className="w-full text-[12px] border-collapse">
                      <thead>
                        <tr className="text-left text-black/40 dark:text-white/40">
                          <th className="font-medium pb-1 pr-2">Field</th>
                          <th className="font-medium pb-1 pr-2">Source</th>
                          <th className="font-medium pb-1">Transform</th>
                        </tr>
                      </thead>
                      <tbody>
                        {output.rows.map((row, i) => (
                          <tr key={i} className="border-t border-black/[0.06] dark:border-white/[0.08] align-top">
                            <td className="py-1 pr-2 font-mono whitespace-nowrap">
                              {row.to}
                              {row.required && <span className="text-red-500"> *</span>}
                            </td>
                            <td className="py-1 pr-2 font-mono text-black/60 dark:text-white/60">{row.source}</td>
                            <td className="py-1 text-black/60 dark:text-white/60">{row.transform || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </AppCard>

        <AppCard className="flex flex-col gap-2" compact>
          <span className="text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">
            Live preview
          </span>
          {!results ? (
            <p className="text-xs text-black/40 dark:text-white/40">
              Paste a sample on the left to see the Signals this mapping would emit.
            </p>
          ) : (
            <PreviewSummary results={results} />
          )}
        </AppCard>
      </div>
    </div>
  );
}

const STATUS_LABEL: Record<PreviewOutputStatus, string> = {
  emitted: 'Emitted',
  'dropped-required': 'Dropped (required field missing)',
  'no-name': 'No name',
  'no-rules': 'No rules',
  'filtered-allowlist': 'Filtered by allowlist',
  'skipped-non-finite': 'Non-finite value',
  'unresolved-signal-ref': 'Targets a signal schema (resolved server-side)',
};

function PreviewSummary({ results }: { results: PreviewRecordResult[] }) {
  const allOutputs = results.flatMap((r) => r.outputs);
  const emitted = allOutputs.filter((o) => o.status === 'emitted').length;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium m-0">
        {results.length} record{results.length === 1 ? '' : 's'} → {emitted} signal{emitted === 1 ? '' : 's'} emitted
        {allOutputs.length > emitted ? `, ${allOutputs.length - emitted} not emitted` : ''}
      </p>
      <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto">
        {results.map((r) => (
          <div key={r.recordIndex} className="flex flex-col gap-1">
            {results.length > 1 && (
              <span className="text-[11px] font-medium text-black/40 dark:text-white/40">Record {r.recordIndex + 1}</span>
            )}
            {r.outputs.map((o) =>
              o.status !== 'emitted' ? (
                <span key={o.outputIndex} className="text-[11px] text-amber-600 dark:text-amber-400">
                  {STATUS_LABEL[o.status]}
                  {o.status === 'unresolved-signal-ref' && o.signalRef ? ` (#${o.signalRef})` : ''}
                </span>
              ) : (
                <div key={o.outputIndex} className="flex flex-wrap items-center gap-1">
                  <Badge className="text-emerald-600 dark:text-emerald-400 border-emerald-600/30">{o.signal!.name}</Badge>
                  {o.signal!.value && <span className="text-[12px] font-mono">{o.signal!.value}</span>}
                  {o.signal!.labels.map((l, i) => (
                    <Badge key={i}>{l}</Badge>
                  ))}
                </div>
              ),
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
